<template>
  <v-dialog
    :value="value"
    max-width="640"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card class="extra-picker">
      <v-card-title class="extra-picker__title">
        {{ $t("extraContent.pickerTitle") }}
      </v-card-title>

      <v-card-text class="extra-picker__body">
        <p class="extra-picker__hint">{{ $t("extraContent.pickerHint") }}</p>

        <v-text-field
          v-model="search"
          :label="$t('extraContent.search')"
          prepend-inner-icon="fi-rr-search"
          outlined
          dense
          clearable
          hide-details
          class="extra-picker__search"
        />

        <div v-if="loading" class="extra-picker__state">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <p v-else-if="failed" class="extra-picker__state">
          {{ $t("extraContent.loadFailed") }}
        </p>
        <p v-else-if="!visibleModules.length" class="extra-picker__state">
          {{ $t("extraContent.noResults") }}
        </p>

        <v-expansion-panels v-else v-model="openPanels" multiple flat>
          <v-expansion-panel v-for="module in visibleModules" :key="module.id">
            <v-expansion-panel-header class="extra-picker__module">
              {{ module.title }}
              <span class="extra-picker__count">
                {{ module.lessons.length }}
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-checkbox
                v-for="lesson in module.lessons"
                :key="lesson.id"
                v-model="picked"
                :value="lesson.id"
                :label="lesson.title"
                :disabled="limitReached && !picked.includes(lesson.id)"
                dense
                hide-details
                class="extra-picker__check"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="extra-picker__foot">
        <span class="extra-picker__selected">
          {{
            $tc("extraContent.selected", picked.length, {
              count: picked.length,
              max,
            })
          }}
        </span>
        <v-spacer />
        <v-btn text :disabled="busy" @click="$emit('input', false)">
          {{ $t("common.cancel") }}
        </v-btn>
        <v-btn color="primary" depressed :loading="busy" @click="confirm">
          {{ confirmLabel || $t("extraContent.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import pitchPartnerApi from "@/shared/pitchPartnerApi";

// Espelha PARTNER_CLASS_NOTE_MAX_EXTRAS da API.
const MAX_EXTRAS = 10;

// Card 17trt6jfxtn: o professor escolhe aulas da biblioteca, de qualquer nível,
// para indicar ao aluno junto da anotação. Devolve [{ id, title }].
export default {
  name: "ExtraContentPicker",

  props: {
    value: { type: Boolean, default: false },
    // Ids já indicados na anotação: o diálogo abre com eles marcados.
    selected: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    confirmLabel: { type: String, default: "" },
  },

  data() {
    return {
      loading: false,
      failed: false,
      modules: [],
      picked: [],
      search: "",
      openPanels: [],
      max: MAX_EXTRAS,
    };
  },

  computed: {
    limitReached() {
      return this.picked.length >= this.max;
    },

    // Com busca, só as aulas que combinam, e os módulos abertos para mostrá-las.
    visibleModules() {
      const term = String(this.search || "")
        .trim()
        .toLowerCase();
      if (!term) return this.modules;

      return this.modules
        .map((module) => ({
          ...module,
          lessons: module.lessons.filter((lesson) =>
            lesson.title.toLowerCase().includes(term)
          ),
        }))
        .filter((module) => module.lessons.length);
    },
  },

  watch: {
    value(open) {
      if (!open) return;
      this.picked = this.selected.map(String);
      this.search = "";
      if (!this.modules.length) this.load();
    },

    search(term) {
      this.openPanels = term
        ? this.visibleModules.map((_, index) => index)
        : [];
    },
  },

  methods: {
    // Só módulos de aluno: o treinamento é exclusivo dos professores e a API
    // recusaria a aula.
    load() {
      this.loading = true;
      this.failed = false;
      return pitchPartnerApi
        .listCourseContent()
        .then((res) => {
          this.modules = (res.data.content || [])
            .filter((module) => module.sharedWithCustomers)
            .map((module) => ({
              id: module.id,
              title: module.title,
              level: Number(module.level),
              lessons: module.lessons || [],
            }))
            .filter((module) => module.lessons.length)
            .sort((a, b) => (a.level || 0) - (b.level || 0));
        })
        .catch(() => {
          this.failed = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    confirm() {
      const titles = new Map();
      this.modules.forEach((module) =>
        module.lessons.forEach((lesson) => titles.set(lesson.id, lesson.title))
      );
      this.$emit(
        "confirm",
        this.picked.map((id) => ({ id, title: titles.get(id) || "" }))
      );
    },
  },
};
</script>

<style scoped>
.extra-picker {
  font-family: "Poppins", sans-serif;
}

.extra-picker__title {
  font-weight: 700;
}

.extra-picker__hint {
  margin: 0 0 12px;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--dk-ink-soft, #6e6a79);
}

.extra-picker__body {
  max-height: 60vh;
}

.extra-picker__search {
  margin-bottom: 12px;
}

.extra-picker__state {
  display: flex;
  justify-content: center;
  padding: 32px 0;
  color: var(--dk-ink-soft, #6e6a79);
}

.extra-picker__module {
  font-weight: 600;
}

.extra-picker__count {
  flex: none;
  margin-left: 8px;
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--dk-ink-soft, #6e6a79);
}

.extra-picker__check {
  margin-top: 4px;
}

.extra-picker__foot {
  padding: 12px 24px 16px;
}

.extra-picker__selected {
  font-size: 0.85rem;
  color: var(--dk-ink-soft, #6e6a79);
}
</style>
